<template>
    <v-dialog
        v-model="show"
        :max-width="isSubscribe ? 520 : 400"
        overlay-color="#000"
        overlay-opacity="0.8"
        :persistent="loading"
        :fullscreen="phoneOnly"
        scrollable
    >
        <v-card class="package-subscribe-dialog px-lg-2 pb-1">
            <v-card-title class="mb-0 pe-10">
                <span>{{ isSubscribe ? 'الاشتراك في الباقة' : 'تجديد الاشتراك' }}</span>
            </v-card-title>
            <v-card-subtitle class="pb-2">
                {{ pkg.name }}
                <span v-if="pkg.current_month"> — {{ pkg.current_month.name }}</span>
            </v-card-subtitle>

            <!-- Wizard steps (subscribe only) -->
            <div v-if="isSubscribe" class="px-4 px-sm-6 mb-2 pt-3">
                <div class="wizard-steps d-flex align-center">
                    <div class="wizard-step flex-grow-1 d-flex align-center">
                        <div class="wizard-dot primary white--text">1</div>
                        <div
                            class="wizard-label ms-2"
                            :class="step === 1 ? 'font-weight-medium' : 'muted--text'"
                        >اختيار المعلمين</div>
                    </div>
                    <div
                        class="wizard-line mx-2"
                        :class="step > 1 ? 'primary' : 'grey lighten-3'"
                    ></div>
                    <div class="wizard-step flex-grow-1 d-flex align-center">
                        <div
                            class="wizard-dot"
                            :class="step >= 2 ? 'primary white--text' : 'grey lighten-3 grey--text text--darken-1'"
                        >2</div>
                        <div
                            class="wizard-label ms-2"
                            :class="step === 2 ? 'font-weight-medium' : 'muted--text'"
                        >طريقة الدفع</div>
                    </div>
                </div>
            </div>

            <v-card-text class="strong-text--text pb-0 pt-3">
                <!-- ========== Step 1: teachers ========== -->
                <template v-if="isSubscribe && step === 1">
                    <!-- <div class="mb-4 details-text text--text">
                        اختر معلماً لكل مادة — اضغط على بطاقة المعلم للاختيار
                    </div> -->

                    <div
                        v-for="subject in subjects"
                        :key="subject.id"
                        class="subject-block mb-5"
                    >
                        <div class="d-flex align-center mb-2">
                            <v-icon small color="primary" class="me-1">mdi-book-open-page-variant</v-icon>
                            <span class="font-weight-medium strong-text--text">{{ subject.name }}</span>
                            <v-spacer></v-spacer>
                            <v-icon
                                v-if="teacherChoices[subject.id]"
                                small
                                color="success"
                            >mdi-check-circle</v-icon>
                        </div>

                        <div class="teacher-grid">
                            <button
                                v-for="teacher in teachersOf(subject)"
                                :key="teacher.id"
                                type="button"
                                class="teacher-card bordered"
                                :class="{
                                    'primary lighten-5 primary--text': teacherChoices[subject.id] === teacher.id,
                                    disabled: loading,
                                }"
                                :disabled="loading"
                                @click="selectTeacher(subject.id, teacher.id)"
                            >
                                <v-avatar size="68" class="mb-2 teacher-avatar">
                                    <v-img v-if="teacher.avatar" class="grey lighten-3" :src="teacher.avatar"></v-img>
                                    <v-icon v-else color="grey" size="36">mdi-account-circle</v-icon>
                                </v-avatar>
                                <div class="teacher-name text-center">{{ teacher.name }}</div>
                                <div
                                    v-if="teacherChoices[subject.id] === teacher.id"
                                    class="selected-badge primary"
                                >
                                    <v-icon size="16" color="white">mdi-check</v-icon>
                                </div>
                            </button>
                        </div>

                        <div
                            v-if="!teachersOf(subject).length"
                            class="details-text muted--text pa-2"
                        >
                            لا يوجد معلمون لهذه المادة
                        </div>
                    </div>
                </template>

                <!-- ========== Step 2 / renew: payment ========== -->
                <template v-if="!isSubscribe || step === 2">
                    <div
                        v-if="pkg.price != null && platform !== 'ios'"
                        class="mb-4 rounded bordered pa-3 d-flex justify-space-between align-center"
                    >
                        <div>
                            <v-icon color="primary" size="20" class="me-2">mdi-cash</v-icon>
                            <span class="details-text strong-text--text">سعر الباقة</span>
                        </div>
                        <div class="strong-text--text" style="font-size: 1.15rem">{{ moneyFormat(pkg.price, true) }}</div>
                    </div>

                    <template v-if="isSubscribe">
                        <div class="mb-4">
                            <div
                                v-for="subject in subjects"
                                :key="'sum-' + subject.id"
                                class="choice-summary bordered rounded pa-2 mb-2 d-flex align-center"
                            >
                                <v-avatar size="36" class="bordered me-3">
                                    <v-img
                                        v-if="selectedTeacher(subject) && selectedTeacher(subject).avatar"
                                        :src="selectedTeacher(subject).avatar"
                                    ></v-img>
                                    <v-icon v-else color="grey" size="24">mdi-account-circle</v-icon>
                                </v-avatar>
                                <div class="flex-grow-1 overflow-hidden">
                                    <div class="small-text muted--text">{{ subject.name }}</div>
                                    <div class="font-weight-medium text-truncate">
                                        {{ selectedTeacher(subject) ? selectedTeacher(subject).name : '—' }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <div v-if="subscriptionTeachers.length" class="my-4">
                            <div
                                v-for="row in subscriptionTeachers"
                                :key="row.subject_id"
                                class="choice-summary bordered rounded pa-2 mb-2 d-flex align-center"
                            >
                                <v-avatar size="36" class="bordered me-3">
                                    <v-img
                                        v-if="row.teacher && row.teacher.avatar"
                                        :src="row.teacher.avatar"
                                    ></v-img>
                                    <v-icon v-else color="grey" size="24">mdi-account-circle</v-icon>
                                </v-avatar>
                                <div class="flex-grow-1 overflow-hidden">
                                    <div class="small-text muted--text">{{ row.subject_name }}</div>
                                    <div class="font-weight-medium text-truncate">
                                        {{ row.teacher ? row.teacher.name : '—' }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <v-list class="choose-payment-method">
                        <v-list-item v-for="method in paymentMethods" :key="method.value" :class="{'v-list-item--active': paymentMethod === method.value, [`${method.color.split(' ')[0]}--text`]: paymentMethod === method.value}" class="bordered rounded py-1 mb-2" v-ripple="phoneOnly" @click="paymentMethod = method.value">
                            <v-list-item-icon class="me-3">
                                <v-btn icon :color="paymentMethod === method.value ? method.color : 'text'">
                                    <svg v-if="paymentMethod === method.value" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true" class="v-icon__svg"><path d="M12,20C7.58,20 4,16.42 4,12C4,7.58 7.58,4 12,4C16.42,4 20,7.58 20,12C20,16.42 16.42,20 12,20M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M12,7C9.24,7 7,9.24 7,12C7,14.76 9.24,17 12,17C14.76,17 17,14.76 17,12C17,9.24 14.76,7 12,7Z"></path></svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true" class="v-icon__svg"><path d="M12,20C7.58,20 4,16.42 4,12C4,7.58 7.58,4 12,4C16.42,4 20,7.58 20,12C20,16.42 16.42,20 12,20M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2Z"></path></svg>
                                </v-btn>
                                <v-icon size="28" :color="method.color || 'primary'" class="ms-1">{{method.icon}}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="strong-text--text" style="font-size: 15px">{{method.title}}</v-list-item-title>
                                <v-list-item-subtitle class="details-text" style="line-height: 1.4!important">{{method.subtitle}}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>

                    <div :class="{'my-4': !!paymentMethod}">
                        <v-expand-transition mode="out-in">
                            <div v-if="paymentMethod === 'wallet'" key="wallet">
                                <div class="mb-2 rounded bordered pa-3 d-flex justify-space-between">
                                    <div>
                                        <v-icon color="primary" size="20" class="me-2">mdi-wallet</v-icon>
                                        <span class="details-text strong-text--text">رصيد المحفظة</span>
                                    </div>
                                    <div>
                                        <v-chip small label outlined :color="noBalance ? 'error' : 'primary'" dark class="ms-3 px-2" style="height: 22px; font-weight: 600;">{{moneyFormat(user.balance, true)}}</v-chip>
                                    </div>
                                </div>
                                <div v-if="noBalance" class="text-center pa-3 rounded bordered mb-3">
                                    <div class="details-text muted--text mb-2">رصيد محفظتك {{ moneyFormat(user.balance, true) }} غير كافي، لإتمام الاشتراك ستحتاج لشحن المبلغ التالي:</div>
                                    <div class="strong-text--text" style="font-size: 1.5rem">{{moneyFormat(pkg.price - user.balance)}} <span class="medium-text muted--text">جنيه</span></div>
                                </div>
                                <v-btn v-if="noBalance" class="mb-4" color="primary" depressed block :to="{name: 'wallet', query: {recharge: user.balance ? pkg.price - user.balance : pkg.price}}">
                                    <v-icon left>mdi-wallet-plus</v-icon>
                                    <span>شحن رصيد المحفظة</span>
                                </v-btn>
                                <div v-if="!noBalance" class="text-center pa-3 rounded bordered">
                                    <div class="details-text muted--text mb-2">سيتم خصم المبلغ التالي من رصيد محفظتك:</div>
                                    <div class="strong-text--text" style="font-size: 1.5rem">{{moneyFormat(pkg.price)}} <span class="medium-text muted--text">جنيه</span></div>
                                </div>
                            </div>
                            <div v-else-if="paymentMethod === 'paycard'" key="paycard">
                                <div class="mb-3 details-text text--text">
                                    أدخل الكود الموجود على بطاقة الدفع:
                                </div>
                                <v-text-field
                                    v-model="code"
                                    :disabled="loading"
                                    prepend-inner-icon="mdi-credit-card-outline"
                                    class="code-input"
                                    color="secondary"
                                    outlined
                                    hide-details
                                    placeholder="كود بطاقة الدفع"
                                ></v-text-field>
                            </div>
                        </v-expand-transition>
                    </div>

                    <v-alert dense text type="text" icon="mdi-information-outline" class="mt-4 details-text py-1">
                        يتم تجديد الباقة شهرياً
                    </v-alert>
                </template>
            </v-card-text>

            <v-card-actions :class="{'px-6 mt-4': pcOnly, 'px-4 mt-4': phoneOnly}" class="flex-column">
                <v-btn icon absolute top left color="muted" :disabled="loading" @click="show = false">
                    <v-icon>{{ phoneOnly ? 'mdi-arrow-left' : 'mdi-close' }}</v-icon>
                </v-btn>

                <template v-if="isSubscribe && step === 1">
                    <v-btn
                        color="primary"
                        large
                        block
                        depressed
                        class="mx-0"
                        :disabled="loading || !allTeachersChosen"
                        @click="goToPayment"
                    >
                        التالي
                        <v-icon left>mdi-arrow-left</v-icon>
                    </v-btn>
                </template>

                <template v-else>
                    <div class="d-flex w-100" style="gap: 8px">
                        <v-btn
                            v-if="isSubscribe"
                            outlined
                            large
                            :disabled="loading"
                            class="flex-grow-0"
                            @click="step = 1"
                        >
                            <v-icon>mdi-arrow-right</v-icon>
                        </v-btn>
                        <v-btn
                            color="success"
                            large
                            :loading="loading"
                            :disabled="loading || !canSubmit"
                            depressed
                            class="flex-grow-1 mx-0"
                            @click="submit"
                        >
                            {{ isSubscribe ? 'تأكيد الاشتراك' : 'تأكيد التجديد' }}
                        </v-btn>
                    </div>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

export default {
    props: {
        value: {},
        pkg: { type: Object, required: true },
        mode: { type: String, default: 'subscribe' }, // subscribe | renew
    },
    data() {
        return {
            show: false,
            loading: false,
            code: '',
            paymentMethod: '',
            teacherChoices: {},
            step: 1,
        }
    },
    computed: {
        ...GlobalComputed,
        isSubscribe() {
            return this.mode !== 'renew'
        },
        subjects() {
            return (this.pkg && this.pkg.subjects) ? this.pkg.subjects : []
        },
        subscriptionTeachers() {
            return (this.pkg && this.pkg.subscription && this.pkg.subscription.teachers)
                ? this.pkg.subscription.teachers
                : []
        },
        allTeachersChosen() {
            if (!this.subjects.length) {
                return false
            }
            return this.subjects.every((subject) => !!this.teacherChoices[subject.id])
        },
        walletAvailable() {
            // wallet needs a real price to charge; a package with no price stays paycard-only
            return !!this.appData.enable_wallet_system
                && this.pkg.price != null
                && this.pkg.price > 0
                && !this.pkg.disable_wallet_purchase
        },
        paymentMethods() {
            let methods = [
                { value: 'paycard', title: 'مجموعات خاصة', subtitle: 'الدفع المباشر بكود بطاقة الدفع الخاص بالمجموعات الخاصة', icon: 'mdi-credit-card-outline', color: 'success darken-1' }
            ]
            if (this.walletAvailable) {
                methods.unshift({ value: 'wallet', title: 'محفظة الحساب', subtitle: 'الدفع برصيد محفظة الحساب الخاص بك', icon: 'mdi-wallet-outline', color: 'primary' })
            }
            return methods
        },
        noBalance() {
            if (!this.walletAvailable) {
                return false
            }
            return this.user.balance < this.pkg.price
        },
        canSubmit() {
            if (this.paymentMethod === 'paycard') {
                if (!this.code) {
                    return false
                }
            } else if (this.paymentMethod === 'wallet') {
                if (this.noBalance) {
                    return false
                }
            } else {
                return false
            }
            if (!this.isSubscribe) {
                return true
            }
            return this.allTeachersChosen
        },
    },
    watch: {
        show(show) {
            if (!show) {
                this.$emit('input', false)
            } else {
                this.step = 1
                this.paymentMethod = ''
                this.initChoices()
                this.autoSelectPaymentMethod()
            }
        },
        value(show) {
            this.show = show
        },
        code(code) {
            if (code && code.search(' ') !== -1) {
                this.code = code.replace(/ /g, '')
            }
        },
        pkg: {
            deep: true,
            handler() {
                if (this.show) {
                    this.initChoices()
                }
            },
        },
    },
    methods: {
        ...GlobalMethods,
        teachersOf(subject) {
            const teachers = subject.teachers || []
            return Array.isArray(teachers) ? teachers : (teachers.data || [])
        },
        selectedTeacher(subject) {
            const id = this.teacherChoices[subject.id]
            if (!id) {
                return null
            }
            return this.teachersOf(subject).find((t) => t.id === id) || null
        },
        selectTeacher(subjectId, teacherId) {
            this.$set(this.teacherChoices, subjectId, teacherId)
        },
        initChoices() {
            const choices = {}
            this.subjects.forEach((subject) => {
                choices[subject.id] = this.teacherChoices[subject.id] || null
            })
            this.teacherChoices = choices
        },
        goToPayment() {
            if (!this.allTeachersChosen) {
                this.showMsg('يجب اختيار معلم لكل مادة', 'error')
                return
            }
            this.step = 2
        },
        extractErrorMessage(err) {
            const data = err && err.response ? err.response.data : null
            if (!data) {
                return 'حدثت مشكلة أثناء تنفيذ طلبك'
            }
            if (typeof data === 'string') {
                return data
            }
            if (data.message) {
                return data.message
            }
            if (data.errors) {
                const key = Object.keys(data.errors)[0]
                const first = data.errors[key]
                if (Array.isArray(first) && first[0]) {
                    return first[0]
                }
            }
            return 'حدثت مشكلة أثناء تنفيذ طلبك'
        },
        autoSelectPaymentMethod() {
            window.setTimeout(() => {
                if (this.paymentMethods.length === 1) {
                    this.paymentMethod = this.paymentMethods[0].value
                }
            }, 500)
        },
        submit() {
            if (!this.canSubmit || this.loading) {
                if (!this.paymentMethod) {
                    this.showMsg('اختر طريقة الدفع', 'error')
                } else if (this.paymentMethod === 'paycard' && !this.code) {
                    this.showMsg('أدخل كود البطاقة بشكل صحيح', 'error')
                } else if (this.paymentMethod === 'wallet' && this.noBalance) {
                    this.showMsg('رصيد المحفظة غير كافي لإتمام العملية', 'error')
                } else if (this.isSubscribe) {
                    this.showMsg('يجب اختيار معلم لكل مادة', 'error')
                }
                return
            }

            this.loading = true
            const id = this.pkg.id
            let action
            let payload = { pay_by: this.paymentMethod }

            if (this.paymentMethod === 'paycard') {
                payload.code = this.code
            }

            if (this.isSubscribe) {
                action = 'subscribePackage'
                payload.subjects = this.subjects.map((subject) => ({
                    subject_id: subject.id,
                    teacher_id: this.teacherChoices[subject.id],
                }))
            } else {
                action = 'renewPackage'
            }

            this.$store.dispatch(action, { id, data: payload }).then(() => {
                this.loading = false
                this.show = false
                this.code = ''
                this.paymentMethod = ''
                this.step = 1
                this.showMsg(
                    this.isSubscribe ? 'تم الاشتراك في الباقة بنجاح، وتم إضافة الدورات إلى دوراتك' : 'تم تجديد الاشتراك بنجاح',
                    'success',
                    false,
                    false,
                    2000
                )
                this.$emit('done')
                if (this.$route.name === 'my-courses') {
                    this.$store.dispatch('getMyCourses').catch(() => {})
                } else if (this.isSubscribe) {
                    this.$router.push({ name: 'my-courses' })
                }
            }).catch((err) => {
                this.loading = false
                this.showMsg(this.extractErrorMessage(err), 'error')
                this.code = ''
            })
        },
    },
    mounted() {
        if (this.value) {
            this.show = true
        }
        this.autoSelectPaymentMethod()
    },
}
</script>

<style lang="scss">
.code-input {
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }
}

.package-subscribe-dialog {
    .wizard-steps {
        .wizard-dot {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.8rem;
            font-weight: 700;
            flex-shrink: 0;
        }

        .wizard-label {
            font-size: 0.8rem;
            white-space: nowrap;
        }

        .wizard-line {
            height: 2px;
            flex: 0 0 28px;
            border-radius: 1px;
        }
    }

    .teacher-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
        gap: 10px;
    }

    .teacher-card {
        position: relative;
        appearance: none;
        background: transparent;
        border-radius: 12px;
        padding: 12px 8px 10px;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        align-items: center;
        color: inherit;
        font: inherit;

        &.primary--text {
            border-color: currentColor !important;
        }

        &.disabled {
            opacity: 0.55;
            cursor: not-allowed;
        }

        .teacher-name {
            font-size: 0.8rem;
            font-weight: 600;
            line-height: 1.25;
            max-width: 100%;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }

        .selected-badge {
            position: absolute;
            top: 6px;
            left: 6px;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
}
</style>
